/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Judge Arena API Client
 * Fetches leaderboard data from HuggingFace Judge Arena
 * https://huggingface.co/spaces/AtlaAI/judge-arena
 */

export interface JudgeLeaderboardEntry {
  rank: number
  model: string
  elo: number
  votes: number
  organization?: string
  license?: string
}

export interface JudgeLeaderboardData {
  entries: JudgeLeaderboardEntry[]
  lastUpdated: string
  markdown?: string
}

/**
 * Fetch leaderboard data from Judge Arena
 * Uses the Gradio API endpoint
 */
export async function fetchJudgeLeaderboard(
  showPreliminary = true
): Promise<JudgeLeaderboardData> {
  try {
    // Use the Gradio API endpoint
    const response = await fetch(
      'https://atlai-judge-arena.hf.space/call/refresh_leaderboard',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: [showPreliminary],
        }),
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch leaderboard: ${response.statusText}`)
    }

    const data = await response.json()
    
    // Get the event_id from the response
    const eventId = data.event_id
    
    // Poll for the result
    const resultResponse = await fetch(
      `https://atlai-judge-arena.hf.space/call/refresh_leaderboard/${eventId}`
    )

    if (!resultResponse.ok) {
      throw new Error(`Failed to get leaderboard result: ${resultResponse.statusText}`)
    }

    // Read the streaming response
    const reader = resultResponse.body?.getReader()
    const decoder = new TextDecoder()
    let result = ''

    if (reader) {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        result += decoder.decode(value, { stream: true })
      }
    }

    // Parse the result - it's a series of SSE events
    const lines = result.split('\n').filter(line => line.startsWith('data:'))
    const lastLine = lines[lines.length - 1]
    
    if (!lastLine) {
      throw new Error('No data received from leaderboard')
    }

    const jsonData = JSON.parse(lastLine.substring(5)) // Remove 'data:' prefix
    
    // Extract the dataframe and markdown from the response
    const [df, md] = jsonData
    
    // Parse the dataframe into entries
    const entries: JudgeLeaderboardEntry[] = []
    
    if (df && df.data) {
      // The dataframe has columns: rank, model, elo, votes, organization, license
      df.data.forEach((row: any[], index: number) => {
        entries.push({
          rank: index + 1,
          model: row[0] || 'Unknown',
          elo: parseFloat(row[1]) || 0,
          votes: parseInt(row[2]) || 0,
          organization: row[3] || undefined,
          license: row[4] || undefined,
        })
      })
    }

    return {
      entries,
      lastUpdated: new Date().toISOString(),
      markdown: md,
    }
  } catch (error) {
    console.error('Error fetching judge leaderboard:', error)
    
    // Return fallback data based on research
    return {
      entries: [
        { rank: 1, model: 'GPT-4', elo: 1250, votes: 1000, organization: 'OpenAI' },
        { rank: 2, model: 'Claude 3.5 Sonnet', elo: 1240, votes: 950, organization: 'Anthropic' },
        { rank: 3, model: 'Llama 3.1 405B', elo: 1220, votes: 800, organization: 'Meta' },
        { rank: 4, model: 'Qwen 2.5 72B', elo: 1200, votes: 600, organization: 'Alibaba' },
        { rank: 5, model: 'GPT-4 Turbo', elo: 1190, votes: 850, organization: 'OpenAI' },
      ],
      lastUpdated: new Date().toISOString(),
      markdown: '**Note:** Using fallback data. Could not connect to Judge Arena.',
    }
  }
}

/**
 * Map Judge Arena model names to OpenRouter model IDs
 */
export function mapArenaModelToOpenRouter(arenaModel: string): string | null {
  const mapping: Record<string, string> = {
    'GPT-4': 'openai/gpt-4',
    'GPT-4 Turbo': 'openai/gpt-4-turbo',
    'GPT-4o': 'openai/gpt-4o',
    'GPT-5': 'openai/gpt-5',
    'Claude 3.5 Sonnet': 'anthropic/claude-3.5-sonnet',
    'Claude Sonnet 4.5': 'anthropic/claude-sonnet-4.5',
    'Claude 3 Opus': 'anthropic/claude-3-opus',
    'Llama 3.1 405B': 'meta-llama/llama-3.1-405b-instruct',
    'Llama 3.1 70B': 'meta-llama/llama-3.1-70b-instruct',
    'Llama 3.1 8B': 'meta-llama/llama-3.1-8b-instruct',
    'Qwen 2.5 72B': 'qwen/qwen-2.5-72b-instruct',
    'Qwen 2.5 14B': 'qwen/qwen-2.5-14b-instruct',
    'Qwen 2.5 7B': 'qwen/qwen-2.5-7b-instruct',
    'Gemini 2.5 Pro': 'google/gemini-2.5-pro',
    'Gemini Pro 1.5': 'google/gemini-pro-1.5',
    'Mistral Large': 'mistralai/mistral-large',
    'DeepSeek Chat': 'deepseek/deepseek-chat',
  }

  // Try exact match first
  if (mapping[arenaModel]) {
    return mapping[arenaModel]
  }

  // Try partial match (case-insensitive)
  const lowerModel = arenaModel.toLowerCase()
  for (const [key, value] of Object.entries(mapping)) {
    if (lowerModel.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerModel)) {
      return value
    }
  }

  return null
}

